import Service, { service } from '@ember/service';
import type { DeviceMessage } from '@envoy/device-sdk';
import type AbilitiesService from 'ember-can/services/abilities';
import type CurrentAdminService from 'garaje/services/current-admin';
import type DeviceStatusService from 'garaje/services/device-status-service';
import type DevicesManagerService from 'garaje/services/devices-manager';
import { type DeviceResponse } from 'garaje/services/devices-manager';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type IntegrationsManagerService from 'garaje/services/integrations-manager';
import type MetricsService from 'garaje/services/metrics';
import { type DashboardChannel, isDashboardChannel, splitEnvelopeType } from 'garaje/utils/mqtt-types';

export interface LocationDispatchContext {
  locationId: string;
  location: unknown; // LocationModel — avoids circular import
  onEntry: (payload: Record<string, unknown>) => void;
  onDeleteInvite: (payload: Record<string, unknown>) => void;
  onNewInvite: (payload: Record<string, unknown>) => void;
  onHubEventReport: (payload: Record<string, unknown>) => void;
  onReconnect: () => void;
}

export interface ZoneDispatchContext {
  zoneId: string;
  onConnectInvite?: (payload: Record<string, unknown>) => void;
}

export default class MqttDispatcherService extends Service {
  @service declare abilities: AbilitiesService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare devicesManager: DevicesManagerService;
  @service('device-status-service') declare deviceStatusService: DeviceStatusService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare integrationsManager: IntegrationsManagerService;
  @service declare metrics: MetricsService;

  #locationContext: LocationDispatchContext | null = null;
  #zoneContext: ZoneDispatchContext | null = null;
  #subscribedLocationId: string | null = null;
  #subscribedZoneId: string | null = null;

  setLocationContext(context: LocationDispatchContext): void {
    this.#locationContext = context;
    this.#subscribedLocationId = context.locationId;
  }

  setZoneContext(context: ZoneDispatchContext): void {
    this.#zoneContext = context;
    this.#subscribedZoneId = context.zoneId;
  }

  clearLocationContext(): void {
    this.#locationContext = null;
    this.#subscribedLocationId = null;
  }

  clearZoneContext(): void {
    this.#zoneContext = null;
    this.#subscribedZoneId = null;
  }

  dispatchLocationMessage(msg: DeviceMessage, currentLocationId: string): void {
    if (this.#subscribedLocationId !== currentLocationId) {
      return; // stale message from a previous location context
    }

    const parsed = splitEnvelopeType(msg.type);
    if (!parsed) {
      console.warn('[MQTT] Message type has no channel separator:', msg.type); // eslint-disable-line no-console
      return;
    }

    const { channel, eventType } = parsed;
    if (!isDashboardChannel(channel)) {
      console.warn('[MQTT] Unknown channel:', channel); // eslint-disable-line no-console
      return;
    }

    this.#routeLocationMessage(channel, eventType, msg.payload);
  }

  dispatchZoneMessage(msg: DeviceMessage, currentZoneId: string): void {
    if (this.#subscribedZoneId !== currentZoneId) {
      return; // stale message from a previous zone context
    }

    const parsed = splitEnvelopeType(msg.type);
    if (!parsed) return;

    const { channel, eventType } = parsed;
    if (!isDashboardChannel(channel)) return;

    this.#routeZoneMessage(channel, eventType, msg.payload);
  }

  // --- Permission gates ---

  ignoreEntryEvent(): boolean {
    return !this.currentAdmin.isEmployee && this.abilities.cannot('view all entries');
  }

  ignoreInviteEvent(): boolean {
    return !this.currentAdmin.isEmployee && this.abilities.cannot('view all invites');
  }

  ignoreEmployeeEvent(): boolean {
    if (this.featureFlags.isEnabled('exclude-workplace-visits')) {
      return true;
    }
    if (this.currentAdmin.isEmployee) {
      return false;
    }
    return this.abilities.cannot('view employee invite in workplace');
  }

  // --- Private routing ---

  #routeLocationMessage(channel: DashboardChannel, eventType: string, payload: Record<string, unknown>): void {
    const ctx = this.#locationContext;
    if (!ctx) return;

    switch (channel) {
      case 'entries':
        if (!this.ignoreEntryEvent()) {
          ctx.onEntry(payload);
        }
        break;

      case 'invites':
        if (eventType === 'delete') {
          ctx.onDeleteInvite(payload);
        } else if (!this.ignoreInviteEvent()) {
          ctx.onNewInvite(payload);
        }
        break;

      case 'employee-visits':
        if (eventType === 'delete') {
          ctx.onDeleteInvite(payload);
        } else if (!this.ignoreEmployeeEvent()) {
          if (payload['v2entry'] || payload['event']) {
            ctx.onEntry(payload);
          } else if (payload['type'] || payload['invite']) {
            ctx.onNewInvite(payload);
          }
        }
        break;

      case 'devices':
        this.#handleDeviceEvent(eventType, payload);
        break;

      case 'integrations':
        this.integrationsManager.handleEvent(
          payload,
          ctx.location as Parameters<IntegrationsManagerService['handleEvent']>[1],
        );
        break;

      case 'connect-invites':
        // connect-invites on a location topic is unexpected but handle gracefully
        break;
    }
  }

  #routeZoneMessage(channel: DashboardChannel, eventType: string, payload: Record<string, unknown>): void {
    const ctx = this.#zoneContext;
    if (!ctx) return;

    switch (channel) {
      case 'devices':
        this.#handleDeviceEvent(eventType, payload);
        break;

      case 'connect-invites':
        ctx.onConnectInvite?.(payload);
        break;

      case 'entries':
      case 'invites':
      case 'employee-visits':
      case 'integrations':
        // These channels are not expected on zone topics
        break;
    }
  }

  #handleDeviceEvent(eventType: string, payload: Record<string, unknown>): void {
    if (eventType === 'connection_status_changed') {
      this.deviceStatusService.onConnectionStatusChanged(
        payload as Parameters<DeviceStatusService['onConnectionStatusChanged']>[0],
      );
    } else {
      this.devicesManager.handleEvent(payload as unknown as DeviceResponse);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'mqtt-dispatcher': MqttDispatcherService;
  }
}
